import { SupportedLanguages } from '../i18n/constants';
import type {
  Benefit,
  BenefitLayoutV2,
  BenefitLayoutV3,
  EventTriggerPayloadType,
  EventTriggerPopupType,
  EventTriggerPopupTypeWithLayout,
  GiftPopupType,
  LayoutType,
  LocalizedEventElement,
  MessagePopupType,
  OfferV2PopupType,
  RoadLocalizedAsset,
} from '../stores/event-trigger/types';
import { isEventTriggerLayoutV2, isEventTriggerLayoutV3, RoadLocalizedAssetKey } from '../stores/event-trigger/types';

type GetLocaleImageWidgetUrlOptionsType = {
  locale: SupportedLanguages;
  benefit: Benefit<EventTriggerPopupType>;
  offerLocalizationFF?: boolean;
  messageLocalizationFF?: boolean;
};

type GetUserEventLocalizedElementOptions<T extends EventTriggerPopupType> = {
  benefit: EventTriggerPayloadType<T>;
  ff?: boolean;
  locale: SupportedLanguages;
};

export const getUserEventLocalizedElement = <T extends EventTriggerPopupTypeWithLayout, L extends LayoutType>(
  options: GetUserEventLocalizedElementOptions<T>,
): LocalizedEventElement<L> | undefined => {
  const { ff, benefit, locale } = options;
  if (!benefit) {
    return;
  }
  return ff && !!benefit?.layout?.localizedElements?.[locale]
    ? (benefit.layout.localizedElements[locale] as LocalizedEventElement<L>)
    : undefined;
};

export const getOfferLayoutElements = (
  localizedElement: LocalizedEventElement<BenefitLayoutV3> | undefined,
  benefit: OfferV2PopupType,
): [string, unknown][] => {
  if (localizedElement) {
    return Object.entries(localizedElement.layout.elements);
  } else {
    return Object.entries(isEventTriggerLayoutV3(benefit.layout) ? benefit.layout.elements : benefit.layout);
  }
};

export const getOfferLayoutSize = (
  localizedElement: LocalizedEventElement<BenefitLayoutV3> | undefined,
  benefit: OfferV2PopupType,
): { imageWidth: number; imageHeight: number } | null => {
  if (localizedElement) {
    return localizedElement.layout.attributes;
  } else {
    return isEventTriggerLayoutV3(benefit.layout) ? benefit.layout.attributes : null;
  }
};

export const getGiftLayoutElements = (
  localizedElement: LocalizedEventElement<BenefitLayoutV2> | undefined,
  benefit: GiftPopupType,
): [string, unknown][] => {
  if (localizedElement) {
    return Object.entries(localizedElement.layout.elements);
  } else {
    return Object.entries(isEventTriggerLayoutV2(benefit.layout) ? benefit.layout.elements : benefit.layout);
  }
};

export const getGiftLayoutSize = (
  localizedElement: LocalizedEventElement<BenefitLayoutV2> | undefined,
  benefit: GiftPopupType,
): { imageWidth: number; imageHeight: number } | null => {
  if (localizedElement) {
    return localizedElement.layout.attributes;
  } else {
    return isEventTriggerLayoutV2(benefit.layout) ? benefit.layout.attributes : null;
  }
};

export const getMessageLayoutElements = (
  localizedElement: LocalizedEventElement<BenefitLayoutV2> | undefined,
  benefit: MessagePopupType,
): [string, unknown][] => {
  if (localizedElement) {
    return Object.entries(localizedElement.layout.elements);
  } else {
    return Object.entries(benefit.layout.elements || {});
  }
};

export const getMessageLayoutSize = (
  localizedElement: LocalizedEventElement<BenefitLayoutV2> | undefined,
  benefit: MessagePopupType,
): { imageWidth: number; imageHeight: number } | null => {
  if (localizedElement) {
    return localizedElement.layout.attributes;
  } else {
    return benefit.layout.attributes;
  }
};

export const getImageWidgetUrl = (options: GetLocaleImageWidgetUrlOptionsType): string => {
  const { locale, offerLocalizationFF, messageLocalizationFF } = options;
  if (options.benefit.benefitType === '2-3-offer' || options.benefit.benefitType === 'rollingOffer') {
    const benefit = options.benefit as EventTriggerPayloadType<'2-3-offer'>;
    const localizedElement = getUserEventLocalizedElement({
      locale,
      ff: offerLocalizationFF,
      benefit,
    });
    if (localizedElement && localizedElement.miniImageUrl) {
      return localizedElement.miniImageUrl;
    }
    return benefit.miniImageUrl;
  }
  if (options.benefit.benefitType === 'message') {
    const benefit = options.benefit as EventTriggerPayloadType<'message'>;
    const localizedElement = getUserEventLocalizedElement({
      locale,
      ff: messageLocalizationFF,
      benefit,
    });
    if (localizedElement && localizedElement.miniImageUrl) {
      return localizedElement.miniImageUrl;
    }
    return benefit.miniImageUrl;
  }
  const benefit = options.benefit as EventTriggerPayloadType<'offer'>;
  return benefit.miniImageUrl;
};

export const getRoadLocalizedUrl = (
  assets: RoadLocalizedAsset[],
  key: RoadLocalizedAssetKey,
  locale: SupportedLanguages,
): string => {
  const localizedAsset = assets.find((asset) => asset.key === key && asset.language === locale);
  if (!localizedAsset) {
    return assets.find((asset) => asset.key === key && asset.language === SupportedLanguages.EN)?.url || '';
  }
  return localizedAsset?.url || '';
};

const REQUIRED_ROAD_ASSET_KEYS = Object.values(RoadLocalizedAssetKey);

export const validateRoadAssets = (
  assets: RoadLocalizedAsset[],
): { valid: boolean; missingKeys: RoadLocalizedAssetKey[] } => {
  const missingKeys = REQUIRED_ROAD_ASSET_KEYS.filter(
    (key) => !assets.some((asset) => asset.key === key && asset.language === SupportedLanguages.EN),
  );

  return { valid: missingKeys.length === 0, missingKeys };
};
